<template>
  <div>
    <Loading type="line" v-if="loading" />

    <div class="kanban-board-scroll">
      <draggable
        tag="div"
        v-model="columns"
        class="kanban-board-columns"
        handle=".kanban-board-drag-handle"
      >
        <div
          v-for="column in columns"
          :key="column.id"
          class="kanban-board-drag-handle"
        >
          <KanbanColumn
            :column="column"
            :columnHeight="columnHeight"
            :loadingTotals="loadingTotals"
            @add-card="openAddCard"
            @open-card="openEditCard"
            @rename="renameStep"
            @delete="deleteStep"
            @card-dropped="onCardDropped"
            @edit-color="openColorModal"
          />
        </div>

        <div v-if="!loading" class="kanban-board-add-column" @click="addStep">
          <i class="fa-solid fa-circle-plus"></i>
          <span>Etapa</span>
        </div>
      </draggable>
    </div>

    <Modal
      :title="cardModalTitle"
      :autoSize="cardModalMode === 'edit'"
      :width="cardModalMode === 'create' ? cardModalWidth : undefined"
      v-if="showModal('cardModal')"
    >
      <CardCreateForm v-if="cardModalMode === 'create'" :step="selectedStep" />

      <template v-if="cardModalMode === 'edit'">
        <div class="kanban-board-step-selector">
          <StepSelector :card="selectedCard" />
        </div>
        <ScrollBar :maxHeight="cardModalMaxHeight">
          <CardEditPanel :key="selectedCard.id" :card="selectedCard" />
        </ScrollBar>
      </template>
    </Modal>

    <Modal title="Cor da Etapa" :width="300" :height="230" v-if="showModal('stepColor')">
      <StepColorPicker
        :id="selectedStepForColor.id"
        :color="selectedStepForColor.color"
        :editMode="true"
      />
    </Modal>
  </div>
</template>

<script>
import Modal from "@/ui/forms/Modal";

import Loading from "@/ui/shared/Loading.vue";
import ScrollBar from "@/ui/layout/ScrollBar.vue";

import draggable from "vuedraggable";
import KanbanColumn from "./KanbanColumn.vue";
import CardCreateForm from "../card/CardCreateForm.vue";
import CardEditPanel from "../card/CardEditPanel.vue";
import StepColorPicker from "../step/StepColorPicker.vue";
import StepSelector from "../step/StepSelector.vue";

import StepService from "../../services/step.service";
import CardService from "../../services/card.service";

import { mapGetters, mapMutations, mapState } from "vuex";

export default {
  name: "KanbanBoard",
  components: {
    Loading,
    Modal,
    ScrollBar,
    draggable,
    KanbanColumn,
    CardCreateForm,
    CardEditPanel,
    StepColorPicker,
    StepSelector,
  },
  props: {
    stringFilter: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      funnelId: this.$route.params.id,
      columns: [],
      columnsSnapshot: [],
      cardModalMode: "",
      selectedStep: {},
      selectedCard: {},
      selectedStepForColor: {},
      loading: false,
      loadingTotals: false,
      browserHeight: window.innerHeight,
    };
  },
  mounted() {
    this.loadBoard();
    window.addEventListener("resize", this.updateHeight);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateHeight);
  },
  computed: {
    ...mapGetters("generic", ["event", "showModal"]),
    ...mapState("generic", ["modal"]),
    columnHeight() {
      return this.browserHeight - 400;
    },
    cardModalTitle() {
      if (this.cardModalMode === "edit") return this.selectedCard.customerName || "Cartão";
      return "Novo Cartão";
    },
    cardModalWidth() {
      return this.cardModalMode === "edit" ? 1200 : 700;
    },
    cardModalMaxHeight() {
      return this.browserHeight - 190;
    },
    hasColumnOrderChanged() {
      if (this.columns.length !== this.columnsSnapshot.length) return true;
      return this.columns.some((col, i) => col.id !== this.columnsSnapshot[i].id);
    },
  },
  methods: {
    ...mapMutations("generic", ["addEvent", "openModal"]),
    async loadBoard() {
      this.loading = true;
      const stepsResponse = await StepService.getAll(this.funnelId);
      const existingCards = this.columns.reduce((acc, col) => {
        acc[col.id] = col.cards;
        return acc;
      }, {});
      this.columns = stepsResponse.data.content.data.map((step) => ({
        ...step,
        cards: existingCards[step.id] || [],
      }));
      this.columnsSnapshot = this.columns.map((c) => ({ id: c.id }));
      this.loading = false;
      await this.loadCards();
      this.recalculateTotals();
    },
    async loadCards() {
      const cardsResponse = await CardService.getAll(this.funnelId, this.stringFilter);
      const cards = cardsResponse.data.content.data;
      this.columns.forEach((column) => {
        column.cards = cards.filter((card) => card.stepId === column.id);
      });
    },
    async addStep() {
      const response = await StepService.create(this.funnelId);
      this.columns.push({
        id: response.data.content.id,
        name: "Novo",
        color: "#577696",
        cards: [],
      });
    },
    async renameStep(step) {
      await StepService.updateName(step.id, step.name);
    },
    async deleteStep(step) {
      await StepService.remove(step.id);
      await this.loadBoard();
    },
    async reorderSteps() {
      const ids = this.columns.map((c) => c.id);
      await StepService.reorder(ids);
      this.columnsSnapshot = this.columns.map((c) => ({ id: c.id }));
    },
    async onCardDropped(event) {
      const rawItem = JSON.parse(JSON.stringify(event.item));
      const cardId = rawItem._underlying_vm_?.id;
      if (!cardId) return;

      let targetStepId = "";
      this.columns.forEach((column) => {
        column.cards.forEach((card) => {
          if (card.id === cardId) targetStepId = column.id;
        });
      });

      if (targetStepId) {
        await CardService.moveToStep(cardId, targetStepId);
        this.recalculateTotals();
      }
    },
    openAddCard(column) {
      this.selectedStep = column;
      this.cardModalMode = "create";
      this.openModal("cardModal");
    },
    openEditCard(card) {
      this.selectedCard = card;
      this.cardModalMode = "edit";
      setTimeout(() => {
        this.openModal("cardModal");
        this.addEvent({ name: "cardSelectedEdit", data: card });
      }, 150);
    },
    openColorModal(step) {
      this.selectedStepForColor = step;
      this.openModal("stepColor");
    },
    recalculateTotals() {
      this.loadingTotals = true;
      setTimeout(() => {
        this.columns.forEach((column) => {
          column.totalRent = column.cards.reduce((sum, c) => sum + (c.totalRent || 0), 0);
          column.totalValue = column.cards.reduce((sum, c) => sum + (c.totalValueRent || 0), 0);
        });
        this.loadingTotals = false;
      }, 800);
    },
    updateHeight() {
      this.browserHeight = window.innerHeight;
    },
  },
  watch: {
    event: {
      handler(event) {
        const boardEvents = ["cardCreated", "responsibleUserUpdated", "colorStepUpdated", "cardRemoved"];
        if (boardEvents.includes(event.name)) this.loadBoard();
        if (event.name === "cardCreated" && event.data && event.data.id) {
          this.selectedCard = event.data;
          this.cardModalMode = "edit";
        }
        if (event.name === "stringFilter") this.loadCards();
      },
      deep: true,
    },
    columns: {
      handler() {
        if (this.hasColumnOrderChanged) this.reorderSteps();
      },
      deep: true,
    },
    "modal.open": {
      handler(isOpen) {
        if (!isOpen && this.modal.name === "cardModal") this.loadBoard();
      },
    },
  },
};
</script>

<style scoped>
.kanban-board-scroll {
  overflow-x: auto;
  top: 16px;
  padding-bottom: 16px;
}

.kanban-board-scroll::-webkit-scrollbar {
  height: 6px;
}

.kanban-board-scroll::-webkit-scrollbar-track {
  background: var(--bg-elevated);
  border-radius: 999px;
}

.kanban-board-scroll::-webkit-scrollbar-thumb {
  background: var(--text-muted);
  border-radius: 999px;
}

.kanban-board-columns {
  display: flex;
  align-items: flex-start;
  width: max-content;
}

.kanban-board-drag-handle {
  cursor: grab;
}

.kanban-board-drag-handle:active {
  cursor: grabbing;
}

.kanban-board-add-column {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  color: var(--color-brand);
  cursor: pointer;
  min-width: 160px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px dashed var(--border-color);
  transition: background 0.15s, border-color 0.15s;
  white-space: nowrap;
}

.kanban-board-add-column:hover {
  background: var(--bg-elevated);
  border-color: var(--color-brand-light);
}

.kanban-board-step-selector {
  margin-top: -25px;
  margin-left: -4px;
  margin-bottom: 20px;
}
</style>


